import { ComponentChildren, Fragment } from 'preact';
import { useState } from 'preact/hooks';
import { FullCourse } from 'server/types';
import { rpx } from 'client/lib/rpx-client';
import { useAsyncState } from 'client/lib/hooks';
import { BtnSecondary } from '@components/buttons';
import { Spinner } from '@components/spinner';
import { IcoCheck } from '@components/icons';
import { HeadingSecondary } from '@course/components/headings';
import { showNextCohortModal } from '@course/components/course-action-modals/next-cohort-modal';
import { URLS } from 'shared/urls';
import {
  buildSetupView,
  ChecklistItem,
  SetupFact,
  SetupFixTarget,
  WeekPlan,
  WeekPlanDay,
} from 'shared/immersion-sprint-setup';

const CARD = 'bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-4 min-w-0';
const LINK = 'text-sm font-medium text-indigo-600 hover:underline';
const WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function settingsUrl(courseId: UUID, target: SetupFixTarget | 'tracker') {
  if (target === 'modules') {
    return URLS.guide.lessons({ courseId, isProduct: false });
  }
  return URLS.guide.coursePage({ courseId, page: `settings/${target}` });
}

function Card({ children }: { children: ComponentChildren }) {
  return <section class={CARD}>{children}</section>;
}

function ChecklistRow({ courseId, item }: { courseId: UUID; item: ChecklistItem }) {
  return (
    <li class="flex items-start gap-3 py-2">
      {item.ready ? (
        <span
          role="img"
          aria-label="Ready"
          class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700"
        >
          <IcoCheck class="w-3.5 h-3.5" />
        </span>
      ) : (
        <span
          role="img"
          aria-label="Needs a fix"
          class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-800"
        >
          !
        </span>
      )}
      <span class="grow min-w-0 text-sm">
        <span class="text-gray-900">{item.label}</span>
        {item.detail && <span class="text-gray-500"> · {item.detail}</span>}
      </span>
      {!item.ready && item.fix && (
        <a class={`${LINK} shrink-0`} href={settingsUrl(courseId, item.fix.target)}>
          {item.fix.label}
        </a>
      )}
    </li>
  );
}

function LaunchChecklist({
  courseId,
  checklist,
  readyCount,
}: {
  courseId: UUID;
  checklist: ChecklistItem[];
  readyCount: number;
}) {
  const allReady = readyCount === checklist.length;
  return (
    <Card>
      <header class="flex items-center justify-between gap-3">
        <h3 class="font-semibold text-gray-900">Launch checklist</h3>
        <span
          class={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
            allReady ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
          }`}
        >
          {readyCount} of {checklist.length} ready
        </span>
      </header>
      <ul class="flex flex-col divide-y divide-gray-100">
        {checklist.map((item) => (
          <ChecklistRow key={item.key} courseId={courseId} item={item} />
        ))}
      </ul>
    </Card>
  );
}

function Mark({ letter, name, class: className }: { letter: string; name: string; class: string }) {
  return (
    <abbr title={name} class={`no-underline font-semibold ${className}`}>
      {letter}
    </abbr>
  );
}

function dayTitle(day: WeekPlanDay) {
  return [...day.lessons, day.holiday].filter(Boolean).join(', ') || undefined;
}

function DayCell({ day }: { day: WeekPlanDay }) {
  return (
    <td
      title={dayTitle(day)}
      class={`border border-gray-100 px-1.5 py-1 align-top ${day.summary ? 'bg-sky-50' : ''} ${
        day.isToday ? 'ring-2 ring-inset ring-indigo-500' : ''
      }`}
    >
      <div class="text-xs text-gray-500 whitespace-nowrap">{day.label}</div>
      <div class="flex flex-wrap gap-1 text-xs min-h-4">
        {day.lessons.length > 0 && <Mark letter="L" name="Lesson" class="text-indigo-700" />}
        {day.standup && <Mark letter="S" name="Standup" class="text-gray-700" />}
        {day.warning && <Mark letter="W" name="Warning" class="text-amber-700" />}
        {day.removal && <Mark letter="R" name="Removal" class="text-red-700" />}
        {day.holiday && <Mark letter="H" name={day.holiday} class="text-purple-700" />}
      </div>
    </td>
  );
}

function WeekPlanCard({ courseId, plan }: { courseId: UUID; plan?: WeekPlan }) {
  return (
    <Card>
      <h3 class="font-semibold text-gray-900">Week plan</h3>
      {!plan ? (
        <p class="text-sm text-gray-600">
          <a class={LINK} href={settingsUrl(courseId, 'checkins')}>
            Set the standup start and end dates to see the week plan.
          </a>
        </p>
      ) : (
        <>
          <div class="overflow-x-auto">
            <table class="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr>
                  <th class="px-1.5 py-1 text-left text-xs font-medium text-gray-500">Week</th>
                  {WEEKDAY_HEADERS.map((name) => (
                    <th key={name} class="px-1.5 py-1 text-left text-xs font-medium text-gray-500">
                      {name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {plan.weeks.map((week) => (
                  <tr key={week.weekStart}>
                    <th
                      scope="row"
                      class="border border-gray-100 px-1.5 py-1 text-left align-top font-normal normal-case"
                    >
                      <div class="font-medium text-gray-900 whitespace-nowrap">
                        Week {week.weekNumber}
                      </div>
                      {week.firstLesson && (
                        <div
                          class="max-w-[8rem] line-clamp-2 text-xs text-gray-500"
                          title={week.firstLesson}
                        >
                          {week.firstLesson}
                        </div>
                      )}
                    </th>
                    {week.days.map((day) => (
                      <DayCell key={day.date} day={day} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {plan.truncated && <p class="text-sm text-gray-600">Showing the first 16 weeks</p>}
          <p class="flex flex-wrap items-center gap-x-1 text-xs text-gray-600">
            <span>
              <strong class="text-indigo-700">L</strong> Lesson
            </span>
            <span>·</span>
            <span>
              <strong class="text-gray-700">S</strong> Standup
            </span>
            <span>·</span>
            <span class="flex items-center gap-1">
              <span
                aria-hidden="true"
                class="inline-block h-3 w-3 border border-sky-200 bg-sky-50"
              />
              Weekly summary window
            </span>
            <span>·</span>
            <span>
              <strong class="text-amber-700">W</strong> Warning
            </span>
            <span>·</span>
            <span>
              <strong class="text-red-700">R</strong> Removal
            </span>
            <span>·</span>
            <span>
              <strong class="text-purple-700">H</strong> Holiday
            </span>
          </p>
        </>
      )}
    </Card>
  );
}

function FactsCard({
  title,
  facts,
  linkLabel,
  href,
}: {
  title: string;
  facts: SetupFact[];
  linkLabel: string;
  href: string;
}) {
  return (
    <Card>
      <h3 class="font-semibold text-gray-900">{title}</h3>
      <dl class="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
        {facts.map((fact) => (
          <Fragment key={fact.label}>
            <dt class="font-semibold text-gray-700">{fact.label}</dt>
            <dd class="min-w-0 break-words">{fact.value}</dd>
          </Fragment>
        ))}
      </dl>
      <a class={LINK} href={href}>
        {linkLabel}
      </a>
    </Card>
  );
}

export function ImmersionSprintSetup({ course }: { course: FullCourse }) {
  const [attempt, setAttempt] = useState(0);
  const [data] = useAsyncState(async () => {
    try {
      return await rpx.courseSettings.getImmersionSprintSetup({ courseId: course.id });
    } catch {
      return 'failed' as const;
    }
  }, [course.id, attempt]);

  if (!data) {
    return (
      <div class="flex justify-center">
        <Spinner class="border-sky-500 w-8 h-8" />
      </div>
    );
  }
  if (data === 'failed') {
    return (
      <div class="flex flex-col items-start gap-3" role="alert">
        <p class="text-sm text-gray-600">Couldn't load the setup. Try again.</p>
        <BtnSecondary onClick={() => setAttempt(attempt + 1)}>Retry</BtnSecondary>
      </div>
    );
  }

  const view = buildSetupView(data);
  return (
    <div class="flex flex-col gap-6 max-w-4xl min-w-0">
      <header class="flex flex-wrap items-center justify-between gap-3">
        <HeadingSecondary>Immersion sprint</HeadingSecondary>
        <BtnSecondary
          onClick={() =>
            showNextCohortModal({
              course: { id: course.id, title: course.title, acesProgram: data.acesProgram },
              startDate: data.standups.startDate,
              endDate: data.standups.endDate,
              today: data.today,
              canCopyPricing: course.level === 'guide',
            })
          }
        >
          Copy as next cohort
        </BtnSecondary>
      </header>
      <LaunchChecklist
        courseId={course.id}
        checklist={view.checklist}
        readyCount={view.readyCount}
      />
      <WeekPlanCard courseId={course.id} plan={view.weekPlan} />
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FactsCard
          title="Standups"
          facts={view.standupFacts}
          linkLabel="Edit standups"
          href={settingsUrl(course.id, 'checkins')}
        />
        <FactsCard
          title="Deadline"
          facts={view.deadlineFacts}
          linkLabel="Edit deadline"
          href={settingsUrl(course.id, 'completion-gate')}
        />
      </div>
      {data.tracker.enabled && (
        <FactsCard
          title="Tracker"
          facts={view.trackerFacts}
          linkLabel="Edit tracker"
          href={settingsUrl(course.id, 'tracker')}
        />
      )}
    </div>
  );
}
